import { BitkitControlButton, BitkitTable, BitkitTooltip, IconArrowRight, IconDownload } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Collapsible } from '@chakra-ui/react/collapsible';
import { Text } from '@chakra-ui/react/text';
import { ReactNode, useEffect } from 'react';

import { DecoratedArtifact } from '@/models/Artifact';
import analyticsEventNames from '@/utils/analyticEventNames';
import { formatDate } from '@/utils/commonFormats';
import { installableArtifactPath } from '@/utils/monolithApiRouteService';

import ImageContent from '../../../ArtifactElement/ImageContent';
import TextContent from '../../../ArtifactElement/TextContent';
import {
  ArtifactDownloadSource,
  useArtifactTracking,
} from '../../../ArtifactTrackingProvider/ArtifactTrackingProvider';
import InstallableContent from './components/InstallableContent';

/** Expand toggle, name, version, size, actions — the span the expanded content has to cover. */
const COLUMN_COUNT = 5;

type Props = {
  appSlug?: string;
  artifact: DecoratedArtifact;
  workflowName: string;
};

type ExpandedContentProps = {
  artifact: DecoratedArtifact;
  children: ReactNode;
  isOpen: boolean;
};

/**
 * BitkitTable.ExpandableRow keeps its collapsed content mounted and exposes no open-state callback, so
 * both halves of v1's `onExpandedChanged` are rebuilt from the row's own Collapsible context:
 * the content components lazy-load off `isOpen`, and the expand event fires from here.
 */
const ExpandedContent = ({ artifact, children, isOpen }: ExpandedContentProps) => {
  const segmentTrack = useArtifactTracking();

  useEffect(() => {
    if (isOpen) {
      segmentTrack('Artifact Item Details Expanded', {
        artifact_slug: artifact.slug,
      });
    }
  }, [artifact.slug, isOpen, segmentTrack]);

  return <>{children}</>;
};

const ArtifactRow = ({ appSlug, artifact, workflowName }: Props) => {
  const segmentTrack = useArtifactTracking();

  const hasAppInfo = artifact.appInfo.length > 0;
  const hasTextContent = artifact.contentType === 'text';
  const hasImageContent = artifact.contentType === 'image';
  const hasInstallableContent = artifact.contentType === 'installable';
  const { isExpired } = artifact;

  const renderContent = (isOpen: boolean): ReactNode => {
    if (hasTextContent) {
      return <TextContent artifact={artifact} isOpen={isOpen} />;
    }

    if (hasImageContent) {
      return <ImageContent artifact={artifact} isOpen={isOpen} />;
    }

    return <InstallableContent artifact={artifact} />;
  };

  let displayExpandableContent = hasTextContent || hasImageContent || hasInstallableContent;
  if (hasInstallableContent && !hasAppInfo) {
    displayExpandableContent = false;
  }

  // The inline preview fetches the object, so an expired artifact would expand
  // into a broken image or empty text.
  if (isExpired) {
    displayExpandableContent = false;
  }

  const expiredMessage = artifact.expiresAt
    ? `This artifact expired on ${formatDate(artifact.expiresAt)} and is no longer available.`
    : 'This artifact expired and is no longer available.';
  const expiredTextColor = isExpired ? 'text/tertiary' : undefined;

  const downloadControl = isExpired ? (
    // A disabled button dispatches no pointer events, so the tooltip needs the focusable wrapper.
    <BitkitTooltip text={expiredMessage} wrapTrigger>
      <BitkitControlButton label="Download" icon={IconDownload} state="disabled" />
    </BitkitTooltip>
  ) : (
    artifact.downloadUrl && (
      <BitkitControlButton
        label="Download"
        download
        href={artifact.downloadUrl}
        icon={IconDownload}
        onClick={() =>
          segmentTrack(analyticsEventNames.artifactDownloadButtonClicked, {
            artifact_type: artifact.type,
            is_installable_artifact: artifact.contentType === 'installable',
            source: ArtifactDownloadSource.buildDetailsArtifactsTab,
          })
        }
      />
    )
  );

  const detailsControl = isExpired ? (
    <BitkitTooltip text={expiredMessage} wrapTrigger>
      <BitkitControlButton label="View artifact" icon={IconArrowRight} state="disabled" />
    </BitkitTooltip>
  ) : (
    <BitkitControlButton
      label="View artifact"
      href={installableArtifactPath(appSlug || '', artifact.slug)}
      icon={IconArrowRight}
    />
  );

  const ExtensionIcon = artifact.extensionIconType;

  const cells = (
    <>
      <BitkitTable.Cell>
        <Box display="flex" alignItems="center" gap="8">
          <ExtensionIcon opacity={isExpired ? 0.5 : 1} />
          <BitkitTooltip text={expiredMessage} disabled={!isExpired}>
            <Text as="p" textAlign="start" textStyle="body/lg/regular" wordBreak="break-all" color={expiredTextColor}>
              {artifact.title}
              {workflowName && (
                <>
                  <br />
                  <Text as="span" textStyle="body/md/regular" color="text/secondary">
                    Generated by {workflowName}
                  </Text>
                </>
              )}
            </Text>
          </BitkitTooltip>
        </Box>
      </BitkitTable.Cell>

      <BitkitTable.Cell color={expiredTextColor}>{hasInstallableContent && artifact.version}</BitkitTable.Cell>

      <BitkitTable.Cell color={expiredTextColor}>{artifact.size}</BitkitTable.Cell>

      <BitkitTable.Cell textAlign="end">
        <Box display="flex" gap="8" justifyContent="end">
          {downloadControl}
          {hasInstallableContent && detailsControl}
        </Box>
      </BitkitTable.Cell>
    </>
  );

  if (!displayExpandableContent) {
    return (
      <BitkitTable.Row>
        <BitkitTable.Cell />
        {cells}
      </BitkitTable.Row>
    );
  }

  return (
    <BitkitTable.ExpandableRow
      colSpan={COLUMN_COUNT}
      expandedContent={
        <Collapsible.Context>
          {({ visible }) => (
            <ExpandedContent artifact={artifact} isOpen={visible}>
              {renderContent(visible)}
            </ExpandedContent>
          )}
        </Collapsible.Context>
      }
    >
      {cells}
    </BitkitTable.ExpandableRow>
  );
};

export default ArtifactRow;
